import React, { useState, useEffect } from 'react';
import { useThemeTokens } from '../theme/ThemeProvider';
import { Button, Card, Tabs, Metric, Chart, DataTable, Progress } from './ui';

const CalidadDatos = () => {
  const { getComponent, getColor, getSpacing, getFontSize, getBorderRadius } = useThemeTokens();
  
  // Estados principales
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedMarketplace, setSelectedMarketplace] = useState('Amazon');
  const [processing, setProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState('');
  const [processingLogs, setProcessingLogs] = useState([]);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  
  // Configuración de procesamiento
  const [maxRows, setMaxRows] = useState(100000);
  const [batchSize, setBatchSize] = useState(10000);
  const [useBatches, setUseBatches] = useState(true);
  
  // Datos de patrones y anomalías
  const [patterns, setPatterns] = useState(null);
  const [anomalies, setAnomalies] = useState(null);

  const styles = {
    container: {
      padding: getSpacing('xl'),
      fontFamily: getComponent('typography').fontFamilies.primary,
      minHeight: '100vh',
      backgroundColor: getColor('neutral.lightGray'),
    },
    header: {
      textAlign: 'center',
      marginBottom: getSpacing('xl'),
    },
    title: {
      fontSize: getFontSize('4xl'),
      fontWeight: '700',
      color: getColor('neutral.black'),
      marginBottom: getSpacing('md'),
    },
    subtitle: {
      fontSize: getFontSize('lg'),
      color: getColor('neutral.mediumGray'),
      maxWidth: '800px',
      margin: '0 auto',
    },
    configCard: {
      backgroundColor: getColor('neutral.white'),
      padding: getSpacing('lg'),
      borderRadius: getBorderRadius('lg'),
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      marginBottom: getSpacing('lg'),
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: getSpacing('md'),
      marginBottom: getSpacing('lg'),
    },
    formGroup: {
      marginBottom: getSpacing('md'),
    },
    label: {
      display: 'block',
      fontSize: getFontSize('sm'),
      fontWeight: '500',
      color: getColor('neutral.darkGray'),
      marginBottom: getSpacing('xs'),
    },
    input: {
      width: '100%',
      padding: getSpacing('sm'),
      borderRadius: getBorderRadius('md'),
      border: `1px solid ${getColor('neutral.mediumGray')}`,
      fontSize: getFontSize('sm'),
      backgroundColor: getColor('neutral.white'),
      boxSizing: 'border-box',
    },
    select: {
      width: '100%',
      padding: getSpacing('sm'),
      borderRadius: getBorderRadius('md'),
      border: `1px solid ${getColor('neutral.mediumGray')}`,
      fontSize: getFontSize('sm'),
      backgroundColor: getColor('neutral.white'),
      boxSizing: 'border-box',
    },
    fileUploadArea: {
      border: `2px dashed ${getColor('neutral.mediumGray')}`,
      borderRadius: getBorderRadius('md'),
      padding: getSpacing('xl'),
      textAlign: 'center',
      backgroundColor: getColor('neutral.lightGray'),
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    fileUploadActive: {
      borderColor: getColor('primary.blue'),
      backgroundColor: getColor('primary.lightBlue'),
    },
    processingContainer: {
      textAlign: 'center',
      padding: getSpacing('xl'),
    },
    logContainer: {
      backgroundColor: getColor('neutral.lightGray'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginTop: getSpacing('md'),
      height: '200px',
      overflow: 'auto',
      fontSize: getFontSize('xs'),
      fontFamily: 'monospace',
    },
    metricsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: getSpacing('md'),
      marginBottom: getSpacing('lg'),
    },
    chartsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: getSpacing('lg'),
      marginBottom: getSpacing('lg'),
    },
    qualityScoreCard: {
      backgroundColor: getColor('neutral.white'),
      padding: getSpacing('lg'),
      borderRadius: getBorderRadius('lg'),
      textAlign: 'center',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    },
    qualityGauge: {
      width: '120px',
      height: '120px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto',
      fontSize: getFontSize('2xl'),
      fontWeight: '700',
      color: getColor('neutral.white'),
      marginBottom: getSpacing('md'),
    },
    infoCard: {
      backgroundColor: getColor('primary.lightBlue'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
      border: `1px solid ${getColor('primary.blue')}`,
    },
    warningCard: {
      backgroundColor: getColor('accent.orange'),
      color: getColor('neutral.white'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
    },
    successCard: {
      backgroundColor: getColor('accent.green'),
      color: getColor('neutral.white'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
    },
    patternCard: {
      backgroundColor: getColor('neutral.white'),
      padding: getSpacing('lg'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
      border: `1px solid ${getColor('neutral.lightGray')}`,
    },
    anomalyItem: {
      padding: getSpacing('md'),
      backgroundColor: getColor('neutral.lightGray'),
      borderRadius: getBorderRadius('sm'),
      marginBottom: getSpacing('sm'),
      borderLeft: `4px solid ${getColor('accent.orange')}`,
    },
    fileInfo: {
      backgroundColor: getColor('neutral.lightGray'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginTop: getSpacing('md'),
    },
  };

  // Marketplaces disponibles
  const marketplaces = ['Amazon', 'Mercadolibre', 'Liverpool', 'Walmart'];

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setProcessingLogs(prev => [...prev, `${timestamp} - ${message}`]);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      setError(null);
    }
  };

  const getQualityColor = (score) => {
    if (score === 5) return getColor('accent.green');
    if (score === 3) return getColor('accent.orange');
    return getColor('accent.orange');
  };

  const getQualityLabel = (score) => {
    if (score === 5) return 'Excelente';
    if (score === 3) return 'Regular';
    return 'Malo';
  };

  const simulateProcessing = async () => {
    setProcessing(true);
    setProcessingProgress(0);
    setProcessingLogs([]);
    setError(null);
    
    const steps = [
      { message: 'Iniciando procesamiento...', progress: 10 },
      { message: 'Cargando archivo...', progress: 25 },
      { message: 'Validando formato de datos...', progress: 40 },
      { message: 'Parseando fechas...', progress: 60 },
      { message: 'Analizando patrones temporales...', progress: 80 },
      { message: 'Generando métricas de calidad...', progress: 95 },
      { message: 'Procesamiento completado', progress: 100 },
    ];
    
    for (const step of steps) {
      addLog(step.message);
      setProcessingMessage(step.message);
      setProcessingProgress(step.progress);
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
    
    // Generar datos mock de resultados
    const mockResults = {
      df_parsed: [
        { fecha: '2024-01-15', ventas: 125, marketplace: selectedMarketplace },
        { fecha: '2024-01-16', ventas: 89, marketplace: selectedMarketplace },
        { fecha: '2024-01-17', ventas: 156, marketplace: selectedMarketplace },
        { fecha: '2024-01-18', ventas: 203, marketplace: selectedMarketplace },
        { fecha: '2024-01-19', ventas: 178, marketplace: selectedMarketplace },
      ],
      metrics: {
        valid_count: 4850,
        invalid_count: 150,
        min_date: new Date('2024-01-01'),
        max_date: new Date('2024-12-31'),
        date_range_days: 365,
        avg_daily_records: 13.3,
        max_daily_records: 45,
        min_daily_records: 2,
      },
      quality_score: 5,
      quality_label: 'Excelente',
      dias_cero_ventas: 0,
      processed_rows: 5000,
      original_file_rows: 5000,
      processing_info: {
        sampled: false,
        total_time_seconds: 12.5,
        rows_per_second: 400,
        batch_processing: useBatches,
        batch_size: batchSize,
        num_batches: useBatches ? Math.ceil(5000 / batchSize) : 1,
      }
    };
    
    // Generar patrones mock
    const mockPatterns = {
      volatility: 0.15,
      has_weekly_pattern: true,
      has_monthly_pattern: true,
      peak_day: 4, // Viernes
      low_day: 0,  // Lunes
      peak_month: 11, // Diciembre
      low_month: 2,   // Marzo
    };
    
    // Generar anomalías mock
    const mockAnomalies = {
      outlier_dates: [new Date('2024-03-15'), new Date('2024-07-04')],
      outlier_counts: [350, 25],
      outlier_percentages: [150.2, -78.5],
      missing_day_sequences: [
        { start: '2024-02-10', end: '2024-02-12', length: 3 },
      ],
      big_change_dates: [new Date('2024-06-01')],
      big_change_percentages: [85.3],
    };
    
    setResults(mockResults);
    setPatterns(mockPatterns);
    setAnomalies(mockAnomalies);
    setProcessing(false);
  };

  // Generar gráficos de frecuencia temporal
  const generateTemporalCharts = () => {
    if (!results) return null;

    const monthlyData = {
      x: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
      y: [120, 89, 156, 203, 178, 234, 156, 289, 234, 267, 345, 389],
      type: 'bar',
      marker: { color: getColor('primary.blue') }
    };

    const weeklyData = {
      x: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'],
      y: [145, 167, 189, 234, 289, 203, 156],
      type: 'bar',
      marker: { color: getColor('primary.darkBlue') }
    };

    const dailyData = {
      x: Array.from({length: 31}, (_, i) => i + 1),
      y: Array.from({length: 31}, () => Math.floor(Math.random() * 200) + 100),
      type: 'bar',
      marker: { color: getColor('accent.green') }
    };

    const timelineData = {
      x: ['2024-01-01', '2024-03-01', '2024-06-01', '2024-09-01', '2024-12-01'],
      y: [150, 180, 220, 200, 250],
      type: 'scatter',
      mode: 'lines+markers',
      line: { color: getColor('primary.blue'), width: 3 },
      marker: { size: 8, color: getColor('primary.blue') }
    };

    return { monthlyData, weeklyData, dailyData, timelineData };
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          Calidad de Datos
        </h1>
        <p style={styles.subtitle}>
          Visualización de frecuencia de ventas según unidades y fecha por marketplace
        </p>
      </div>

      {/* Configuración de procesamiento */}
      <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
        <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
          Configuración de procesamiento
        </h3>
        
        <div style={styles.formGrid}>
          <div>
            <label style={styles.label}>Número máximo de filas a procesar:</label>
            <input
              type="number"
              min="1000"
              max="1000000"
              step="10000"
              value={maxRows}
              onChange={(e) => setMaxRows(parseInt(e.target.value))}
              style={styles.input}
            />
          </div>
          <div>
            <label style={styles.label}>Tamaño de lote para procesamiento:</label>
            <input
              type="number"
              min="1000"
              max="100000"
              step="1000"
              value={batchSize}
              onChange={(e) => setBatchSize(parseInt(e.target.value))}
              style={styles.input}
            />
          </div>
        </div>
        
        <div style={styles.formGroup}>
          <label style={{ display: 'flex', alignItems: 'center', gap: getSpacing('sm') }}>
            <input
              type="checkbox"
              checked={useBatches}
              onChange={(e) => setUseBatches(e.target.checked)}
            />
            Usar procesamiento por lotes para archivos grandes
          </label>
        </div>
      </Card>

      {/* Selección de marketplace */}
      <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
        <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
          Configuración de Análisis
        </h3>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Selecciona un marketplace:</label>
          <select
            value={selectedMarketplace}
            onChange={(e) => setSelectedMarketplace(e.target.value)}
            style={styles.select}
          >
            {marketplaces.map(marketplace => (
              <option key={marketplace} value={marketplace}>{marketplace}</option>
            ))}
          </select>
        </div>

        <div style={styles.infoCard}>
          <strong>Marketplace seleccionado: {selectedMarketplace}</strong>
        </div>
      </Card>

      {/* Carga de archivo */}
      <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
        <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
          Cargar Archivo de Ventas
        </h3>
        
        <div 
          style={{
            ...styles.fileUploadArea,
            ...(uploadedFile ? styles.fileUploadActive : {})
          }}
          onClick={() => document.getElementById('file-upload').click()}
        >
          <input
            id="file-upload"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          {uploadedFile ? (
            <div>
              <p>✅ Archivo seleccionado: {uploadedFile.name}</p>
              <p style={{ fontSize: getFontSize('sm') }}>
                Tamaño: {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
              </p>
            </div>
          ) : (
            <div>
              <p>📁 Haz clic para seleccionar un archivo de ventas</p>
              <p style={{ fontSize: getFontSize('sm'), color: getColor('neutral.mediumGray') }}>
                Formatos soportados: CSV, XLSX, XLS
              </p>
            </div>
          )}
        </div>

        {uploadedFile && (
          <div style={styles.fileInfo}>
            <h4 style={{ fontSize: getFontSize('md'), marginBottom: getSpacing('sm') }}>
              Información del archivo:
            </h4>
            <p><strong>Nombre:</strong> {uploadedFile.name}</p>
            <p><strong>Tamaño:</strong> {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
            <p><strong>Tipo:</strong> {uploadedFile.type}</p>
            <p><strong>Marketplace:</strong> {selectedMarketplace}</p>
          </div>
        )}

        <div style={{ marginTop: getSpacing('lg') }}>
          <Button
            onClick={simulateProcessing}
            disabled={!uploadedFile || processing}
            size="large"
          >
            {processing ? 'Procesando...' : 'Procesar Archivo'}
          </Button>
        </div>
      </Card>

      {/* Procesamiento en curso */}
      {processing && (
        <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
          <div style={styles.processingContainer}>
            <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
              Procesando datos de {selectedMarketplace}...
            </h3>
            
            <Progress value={processingProgress} showPercentage />
            
            <p style={{ marginTop: getSpacing('md'), fontSize: getFontSize('lg') }}>
              {processingMessage}
            </p>
            
            <div style={styles.logContainer}>
              {processingLogs.map((log, index) => (
                <div key={index}>{log}</div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Resultados */}
      {results && !processing && (
        <>
          {/* Información de procesamiento */}
          <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
            <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
              Información de Procesamiento
            </h3>
            
            <div style={styles.metricsGrid}>
              <Metric
                title={results.processing_info.sampled ? "Archivo muestreado" : "Archivo procesado completo"}
                value={results.processed_rows}
                format="number"
                suffix=" filas"
              />
              <Metric
                title="Tiempo de procesamiento"
                value={results.processing_info.total_time_seconds}
                format="number"
                suffix=" segundos"
              />
              <Metric
                title="Velocidad"
                value={results.processing_info.rows_per_second}
                format="number"
                suffix=" filas/segundo"
              />
              {results.processing_info.batch_processing && (
                <Metric
                  title="Procesado en lotes"
                  value={results.processing_info.num_batches}
                  format="number"
                  suffix={` lotes de ${results.processing_info.batch_size}`}
                />
              )}
            </div>

            {results.processing_info.sampled && (
              <div style={styles.infoCard}>
                <strong>Archivo muestreado:</strong> {results.processed_rows.toLocaleString()} de {results.original_file_rows.toLocaleString()} filas ({((results.processed_rows / results.original_file_rows) * 100).toFixed(1)}%)
              </div>
            )}
          </Card>

          {/* Métricas de calidad */}
          <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
            <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
              Métricas de Calidad de Datos
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: getSpacing('lg') }}>
              <div style={styles.qualityScoreCard}>
                <h4 style={{ fontSize: getFontSize('md'), marginBottom: getSpacing('md') }}>
                  Calidad: {results.quality_label}
                </h4>
                <div style={{
                  ...styles.qualityGauge,
                  backgroundColor: getQualityColor(results.quality_score)
                }}>
                  {results.quality_score}/5
                </div>
                <p style={{ fontSize: getFontSize('sm'), color: getColor('neutral.mediumGray') }}>
                  Días sin ventas: {results.dias_cero_ventas}
                </p>
              </div>

              <div>
                <h4 style={{ fontSize: getFontSize('md'), marginBottom: getSpacing('md') }}>
                  Validez de fechas
                </h4>
                <div style={styles.metricsGrid}>
                  <Metric
                    title="Fechas válidas"
                    value={results.metrics.valid_count}
                    format="number"
                    color={getColor('accent.green')}
                  />
                  <Metric
                    title="Fechas inválidas"
                    value={results.metrics.invalid_count}
                    format="number"
                    color={getColor('accent.orange')}
                  />
                  <Metric
                    title="Porcentaje válido"
                    value={((results.metrics.valid_count / (results.metrics.valid_count + results.metrics.invalid_count)) * 100).toFixed(1)}
                    format="number"
                    suffix="%"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Análisis de Frecuencia Temporal */}
          <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
            <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
              Análisis de Frecuencia Temporal
            </h3>
            
            <Tabs>
              <Tabs.TabPanel label="Por Mes" icon="📅">
                <Chart
                  data={[generateTemporalCharts()?.monthlyData]}
                  layout={{
                    title: 'Frecuencia de ventas por mes',
                    xaxis: { title: 'Mes' },
                    yaxis: { title: 'Número de registros' }
                  }}
                  height={400}
                />
              </Tabs.TabPanel>
              
              <Tabs.TabPanel label="Por Día de Semana" icon="📊">
                <Chart
                  data={[generateTemporalCharts()?.weeklyData]}
                  layout={{
                    title: 'Frecuencia de ventas por día de semana',
                    xaxis: { title: 'Día de la semana' },
                    yaxis: { title: 'Número de registros' }
                  }}
                  height={400}
                />
              </Tabs.TabPanel>
              
              <Tabs.TabPanel label="Por Día del Mes" icon="📈">
                <Chart
                  data={[generateTemporalCharts()?.dailyData]}
                  layout={{
                    title: 'Frecuencia de ventas por día del mes',
                    xaxis: { title: 'Día del mes' },
                    yaxis: { title: 'Número de registros' }
                  }}
                  height={400}
                />
              </Tabs.TabPanel>
              
              <Tabs.TabPanel label="Serie Temporal" icon="📉">
                <Chart
                  data={[generateTemporalCharts()?.timelineData]}
                  layout={{
                    title: 'Volumen de ventas diarias',
                    xaxis: { title: 'Fecha' },
                    yaxis: { title: 'Número de registros' }
                  }}
                  height={400}
                />
              </Tabs.TabPanel>
            </Tabs>
          </Card>

          {/* Patrones Temporales */}
          {patterns && (
            <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
              <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
                Patrones Temporales Detectados
              </h3>
              
              <div style={styles.metricsGrid}>
                <Metric
                  title="Volatilidad Diaria"
                  value={(patterns.volatility * 100).toFixed(1)}
                  format="number"
                  suffix="%"
                />
                <Metric
                  title="Patrón Semanal"
                  value={patterns.has_weekly_pattern ? "Sí" : "No"}
                  format="text"
                  color={patterns.has_weekly_pattern ? getColor('accent.green') : getColor('neutral.mediumGray')}
                />
                <Metric
                  title="Patrón Mensual"
                  value={patterns.has_monthly_pattern ? "Sí" : "No"}
                  format="text"
                  color={patterns.has_monthly_pattern ? getColor('accent.green') : getColor('neutral.mediumGray')}
                />
              </div>

              {patterns.has_weekly_pattern && (
                <div style={styles.infoCard}>
                  <strong>Patrón semanal detectado:</strong><br />
                  Mayor actividad: <strong>Viernes</strong>. Menor actividad: <strong>Lunes</strong>.
                </div>
              )}

              {patterns.has_monthly_pattern && (
                <div style={styles.infoCard}>
                  <strong>Patrón mensual detectado:</strong><br />
                  Mayor actividad: <strong>Diciembre</strong>. Menor actividad: <strong>Marzo</strong>.
                </div>
              )}
            </Card>
          )}

          {/* Anomalías Detectadas */}
          {anomalies && (
            <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
              <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
                Anomalías Detectadas
              </h3>
              
              {anomalies.outlier_dates && anomalies.outlier_dates.length > 0 && (
                <div style={styles.patternCard}>
                  <h4 style={{ fontSize: getFontSize('md'), marginBottom: getSpacing('sm') }}>
                    Días con actividad inusual:
                  </h4>
                  {anomalies.outlier_dates.slice(0, 5).map((date, index) => (
                    <div key={index} style={styles.anomalyItem}>
                      <strong>{date.toLocaleDateString()}</strong>: {anomalies.outlier_counts[index]} ventas 
                      ({anomalies.outlier_percentages[index] > 0 ? '+' : ''}{anomalies.outlier_percentages[index].toFixed(1)}% respecto al promedio)
                    </div>
                  ))}
                </div>
              )}

              {anomalies.missing_day_sequences && anomalies.missing_day_sequences.length > 0 && (
                <div style={styles.patternCard}>
                  <h4 style={{ fontSize: getFontSize('md'), marginBottom: getSpacing('sm') }}>
                    Períodos sin actividad:
                  </h4>
                  {anomalies.missing_day_sequences.slice(0, 3).map((seq, index) => (
                    <div key={index} style={styles.anomalyItem}>
                      Del <strong>{seq.start}</strong> al <strong>{seq.end}</strong> ({seq.length} días)
                    </div>
                  ))}
                </div>
              )}

              {anomalies.big_change_dates && anomalies.big_change_dates.length > 0 && (
                <div style={styles.patternCard}>
                  <h4 style={{ fontSize: getFontSize('md'), marginBottom: getSpacing('sm') }}>
                    Cambios bruscos en la actividad:
                  </h4>
                  {anomalies.big_change_dates.slice(0, 5).map((date, index) => (
                    <div key={index} style={styles.anomalyItem}>
                      <strong>{date.toLocaleDateString()}</strong>: cambio del {anomalies.big_change_percentages[index]}% respecto al día anterior
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Estadísticas Adicionales */}
          <Card style={{ padding: getSpacing('lg'), marginBottom: getSpacing('lg') }}>
            <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
              Estadísticas Adicionales
            </h3>
            
            <div style={styles.metricsGrid}>
              <Metric
                title="Fecha más antigua"
                value={results.metrics.min_date.toLocaleDateString()}
                format="text"
              />
              <Metric
                title="Fecha más reciente"
                value={results.metrics.max_date.toLocaleDateString()}
                format="text"
              />
              <Metric
                title="Rango de días"
                value={results.metrics.date_range_days}
                format="number"
              />
              <Metric
                title="Promedio diario"
                value={results.metrics.avg_daily_records.toFixed(1)}
                format="number"
              />
              <Metric
                title="Máximo diario"
                value={results.metrics.max_daily_records}
                format="number"
              />
              <Metric
                title="Mínimo diario"
                value={results.metrics.min_daily_records}
                format="number"
              />
            </div>
          </Card>

          {/* Muestra de datos procesados */}
          <Card style={{ padding: getSpacing('lg') }}>
            <h3 style={{ fontSize: getFontSize('lg'), marginBottom: getSpacing('md') }}>
              Muestra de datos procesados
            </h3>
            <DataTable
              data={results.df_parsed}
              columns={Object.keys(results.df_parsed[0])}
            />
          </Card>
        </>
      )}

      {/* Mensaje inicial */}
      {!uploadedFile && !processing && !results && (
        <Card style={{ padding: getSpacing('xl'), textAlign: 'center' }}>
          <p style={{ fontSize: getFontSize('lg'), color: getColor('neutral.mediumGray') }}>
            Carga un archivo CSV o Excel para visualizar la frecuencia de ventas.
          </p>
        </Card>
      )}

      {/* Errores */}
      {error && (
        <div style={styles.warningCard}>
          <strong>❌ Error:</strong> {error}
        </div>
      )}
    </div>
  );
};

export default CalidadDatos;